/**
 * longeva-menu-polish.js — premium menu micro-interactions.
 * Mobile drawer: staggered reveal, submenu scroll-snap, brass tap-line.
 * Desktop mega-menu: column fade-in stagger, cursor-trailing brass indicator.
 * ~1.4KB gzip. Respects prefers-reduced-motion. AbortController cleanup.
 */
(function () {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let rootAC = new AbortController();
  const sig = () => ({ signal: rootAC.signal });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      rootAC.abort();
      rootAC = new AbortController();
    }
  });

  function initStaggeredReveal() {
    if (reduced) return;
    const details = document.querySelector('#Details-menu-drawer-container');
    if (!details) return;
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName !== 'class' || !details.classList.contains('menu-open')) continue;
        requestAnimationFrame(() => {
          details.querySelectorAll('.menu-drawer__animated-element').forEach((el, i) => {
            if (el instanceof HTMLElement) el.style.animationDelay = `${i * 40}ms`;
          });
        });
      }
    });
    mo.observe(details, { attributes: true, attributeFilter: ['class'] });
    rootAC.signal.addEventListener('abort', () => mo.disconnect());
  }

  function initSubmenuScrollSnap() {
    if (reduced) return;
    document.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;
      const summary = e.target.closest('summary[on\\:click="header-drawer/open/submenu"]');
      if (!summary) return;
      const details = summary.closest('details');
      if (!details) return;
      setTimeout(() => {
        requestAnimationFrame(() => {
          const btn = details.querySelector('.menu-drawer__back-button');
          if (btn instanceof HTMLElement) btn.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }, 50);
    }, sig());
  }

  function initBrassLineTap() {
    if (reduced) return;
    document.addEventListener('pointerdown', (e) => {
      if (!(e.target instanceof Element)) return;
      const item = e.target.closest('.menu-drawer__menu-item');
      if (!(item instanceof HTMLElement) || item.querySelector('.lmp-tap-line')) return;
      const prevPos = getComputedStyle(item).position;
      if (prevPos === 'static') item.style.position = 'relative';
      const line = Object.assign(document.createElement('span'), { className: 'lmp-tap-line' });
      line.setAttribute('aria-hidden', 'true');
      line.style.cssText = 'position:absolute;bottom:0;left:0;width:100%;height:1px;background:#B99157;transform:scaleX(0);transform-origin:left center;pointer-events:none;will-change:transform,opacity';
      item.appendChild(line);
      requestAnimationFrame(() => {
        line.animate(
          [{ transform: 'scaleX(0)', opacity: 1 }, { transform: 'scaleX(1)', opacity: 1 }],
          { duration: 180, easing: 'cubic-bezier(0.4,0,0.2,1)', fill: 'forwards' }
        ).finished.then(() =>
          line.animate(
            [{ transform: 'scaleX(1)', transformOrigin: 'right center', opacity: 1 }, { transform: 'scaleX(0)', transformOrigin: 'right center', opacity: 0 }],
            { duration: 180, easing: 'cubic-bezier(0.4,0,0.2,1)', fill: 'forwards' }
          ).finished
        ).then(() => {
          line.remove();
          if (prevPos === 'static') item.style.removeProperty('position');
        });
      });
    }, sig());
  }

  function initMegaMenuFadeIn() {
    if (reduced) return;
    const headerMenu = document.querySelector('header-menu');
    if (!headerMenu) return;
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== 'attributes' || m.attributeName !== 'aria-expanded' || !(m.target instanceof HTMLElement)) continue;
        if (m.target.ariaExpanded !== 'true') continue;
        const submenu = m.target.parentElement?.querySelector('[ref="submenu[]"]');
        if (!(submenu instanceof HTMLElement)) continue;
        requestAnimationFrame(() => {
          submenu.querySelectorAll('.mega-menu__link, .mega-menu__content-list-item').forEach((el, i) => {
            if (el instanceof HTMLElement) {
              el.animate(
                [{ opacity: '0', transform: 'translateY(6px)' }, { opacity: '1', transform: 'translateY(0)' }],
                { duration: 200, delay: i * 30, easing: 'ease-out', fill: 'backwards' }
              );
            }
          });
        });
      }
    });
    headerMenu.querySelectorAll('[ref="menuitem"]').forEach((el) =>
      mo.observe(el, { attributes: true, attributeFilter: ['aria-expanded'] })
    );
    rootAC.signal.addEventListener('abort', () => mo.disconnect());
  }

  function initBrassIndicator() {
    if (reduced) return;
    const headerMenu = document.querySelector('header-menu');
    if (!headerMenu) return;
    const ind = Object.assign(document.createElement('span'), { className: 'lmp-brass-indicator' });
    ind.setAttribute('aria-hidden', 'true');
    ind.style.cssText = 'position:fixed;height:0.5px;background:#B99157;pointer-events:none;transition:left 180ms cubic-bezier(0.4,0,0.2,1),width 180ms cubic-bezier(0.4,0,0.2,1),top 180ms cubic-bezier(0.4,0,0.2,1),opacity 140ms ease;opacity:0;z-index:9999;will-change:left,width,opacity';
    document.body.appendChild(ind);

    function show(listItem) {
      const link = listItem.querySelector('[ref="menuitem"]');
      if (!(link instanceof HTMLElement)) return;
      const r = link.getBoundingClientRect();
      ind.style.top = `${r.bottom - 0.5}px`;
      ind.style.left = `${r.left}px`;
      ind.style.width = `${r.width}px`;
      ind.style.opacity = '1';
    }
    function hide() { ind.style.opacity = '0'; }

    headerMenu.addEventListener('pointerenter', (e) => {
      if (!(e.target instanceof Element)) return;
      const li = e.target.closest('.menu-list__list-item');
      if (li instanceof HTMLElement) requestAnimationFrame(() => show(li));
    }, { capture: true, ...sig() });

    headerMenu.addEventListener('pointerleave', hide, sig());

    const closeMO = new MutationObserver(() => { if (headerMenu.ariaExpanded !== 'true') hide(); });
    closeMO.observe(headerMenu, { attributes: true, attributeFilter: ['aria-expanded'] });
    rootAC.signal.addEventListener('abort', () => { closeMO.disconnect(); ind.remove(); });
  }

  // ──────────────────────────────────────────────────────────────────────
  // Menu port (external reference) — three additional behaviours layered onto Horizon's
  // native menu JS. Each one is gated behind feature checks + AbortController
  // cleanup so they fail silently if the DOM shape isn't what we expect.
  // See ~/notes/plans/sleepy-brewing-scone.md for the full spec.
  // ──────────────────────────────────────────────────────────────────────

  // §10.3 — Body scroll lock while mobile drawer is open. Adds the
  // .longeva-drawer-open class on <body> so §10.3 CSS can suppress rubber
  // banding on iOS Safari. Watches the drawer's outer <details> open attr.
  function initDrawerBodyLock() {
    const drawer = document.querySelector('#Details-menu-drawer-container');
    if (!drawer) return;
    const sync = () => {
      document.body.classList.toggle('longeva-drawer-open', drawer.hasAttribute('open'));
    };
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    rootAC.signal.addEventListener('abort', () => {
      mo.disconnect();
      document.body.classList.remove('longeva-drawer-open');
    });
  }

  // §10.2 — Desktop mega-menu hover-intent. Mirrors the reference theme's data-menu-sensitivity
  // (~120ms). Delays the pointerenter "open" signal so brushing past a top-level
  // nav item doesn't accidentally open its mega-menu. Does NOT stop propagation
  // (would break initBrassIndicator). Horizon's :hover/:focus-within still
  // handles instant opens; this rules-out the accidental graze.
  function initHoverIntent() {
    if (reduced) return;
    const headerMenu = document.querySelector('header-menu');
    if (!headerMenu) return;
    const items = headerMenu.querySelectorAll('.menu-list__list-item:has(.menu-list__submenu)');
    if (!items.length) return;
    const HOVER_INTENT_MS = 120;
    items.forEach((li) => {
      let timer = null;
      li.addEventListener('pointerenter', () => {
        if (timer) clearTimeout(timer);
        li.dataset.longevaHoverPending = '1';
        timer = setTimeout(() => {
          delete li.dataset.longevaHoverPending;
        }, HOVER_INTENT_MS);
      }, sig());
      li.addEventListener('pointerleave', () => {
        if (timer) clearTimeout(timer);
        delete li.dataset.longevaHoverPending;
      }, sig());
    });
  }

  // §10.4 — Sticky header show/hide. Writes data-sticky-direction="up"|"down"
  // on #header-component as the user scrolls. CSS in §10.4 collapses
  // .header__row--bottom on "down" past 200px. Mirrors the reference theme's main.js:1029-1041
  // pattern (5px hysteresis). Skips when drawer is open or sticky-state is idle.
  function initStickyShowHide() {
    const header = document.querySelector('#header-component');
    if (!header) return;
    let lastY = window.scrollY;
    const onScroll = () => {
      if (document.body.classList.contains('longeva-drawer-open')) return;
      const y = window.scrollY;
      if (y < 200) {
        header.setAttribute('data-sticky-direction', 'up');
      } else if (lastY < y) {
        header.setAttribute('data-sticky-direction', 'down');
      } else if (lastY - 5 > y) {
        header.setAttribute('data-sticky-direction', 'up');
      }
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true, ...sig() });
    onScroll();
  }

  // ──────────────────────────────────────────────────────────────────────
  // §10.5 — Brand mega-menu editorial pill-bar rebuild.
  //
  // Detects the Shop by Brand mega-menu (first parent label starts with
  // "Most Popular"), then rebuilds its inner DOM into:
  //   <div class="lv-brand-pill-bar">
  //     <button class="lv-brand-pill" data-letter="all" aria-selected>All</button>
  //     <button class="lv-brand-pill" data-letter="A">A</button>...
  //     <div class="lv-brand-search-wrap">
  //       <input class="lv-brand-search" type="search">
  //     </div>
  //   </div>
  //   <div class="lv-brand-scroll-area">
  //     <section class="lv-brand-section lv-brand-section--popular" data-letter="all">
  //       <h3 class="lv-brand-section__title">Most Popular</h3>
  //       <ul class="lv-brand-grid"><li><a class="lv-brand-link">...</a></li></ul>
  //     </section>
  //     <section class="lv-brand-section" data-letter="A" id="lv-brand-letter-A">
  //       <h3 class="lv-brand-section__title">A</h3>
  //       <ul class="lv-brand-grid">...</ul>
  //     </section>
  //     ...
  //   </div>
  // The original .mega-menu__list is hidden via CSS, not removed.
  // On mobile, sections become <details><summary> for collapse.
  // ──────────────────────────────────────────────────────────────────────
  function initBrandPillBar() {
    // DISABLED 2026-05-16 (#42): the desktop A-Z rebuild harvests
    // brands from .mega-menu__column > ul.list-unstyled > a.mega-menu__link.
    // Against Horizon's actual Shop-by-Brand markup this produced empty
    // letter sections with huge gaps (see screenshot) — the SAME failure
    // the mobile variant was disabled for on 2026-05-14 (initBrandPillBarMobile).
    // A fragile bespoke scroller I cannot verify is worse than the clean,
    // reliable Horizon columnar mega-menu, which §10.2 (the #42 flex fix)
    // now renders premium and content-hugging — identical to the working
    // "Shop by Treatment" menu. data-lv-brand-az is never set, so the
    // §10.5 scroller CSS stays dormant and the brand menu falls back to
    // that clean columns layout. Re-enable only after live-DOM-verified
    // harvesting against the real Horizon Brand menu structure.
    return;
    /* eslint-disable */
    const grids = document.querySelectorAll('.mega-menu__grid');
    const isMobile = window.matchMedia('(max-width: 989px)').matches;

    grids.forEach((grid) => {
      if (!(grid instanceof HTMLElement)) return;
      // Idempotent guard: skip if our pill bar is already present
      if (grid.querySelector(':scope > .lv-brand-pill-bar')) return;

      const firstParent = grid.querySelector(
        '.mega-menu__column .mega-menu__link--parent .wrap-text, .mega-menu__column .mega-menu__link--parent'
      );
      if (!firstParent) return;
      const firstTitle = firstParent.textContent.trim().toLowerCase();
      if (!firstTitle.startsWith('most popular')) return;

      grid.dataset.lvBrandAz = '';

      // Collect columns into structured data
      const columns = Array.from(grid.querySelectorAll('.mega-menu__column'));
      const sections = [];
      columns.forEach((col, i) => {
        if (!(col instanceof HTMLElement)) return;
        const parentEl = col.querySelector('.mega-menu__link--parent .wrap-text, .mega-menu__link--parent');
        const rawLabel = parentEl ? parentEl.textContent.trim() : '';
        const isPopular = i === 0 || rawLabel.toLowerCase().startsWith('most popular');
        const letterKey = isPopular ? 'all' : rawLabel;
        const displayTitle = isPopular ? 'Most Popular' : rawLabel;
        const brandLinks = Array.from(col.querySelectorAll('ul.list-unstyled > li > a.mega-menu__link'));
        if (brandLinks.length === 0) return;
        const brands = brandLinks.map((a) => {
          const span = a.querySelector('.wrap-text');
          return {
            name: (span ? span.textContent : a.textContent).trim(),
            href: a.getAttribute('href') || '#',
          };
        });
        sections.push({ key: letterKey, title: displayTitle, brands, isPopular });
      });

      if (sections.length === 0) return;

      // Build pill bar
      const pillBar = document.createElement('div');
      pillBar.className = 'lv-brand-pill-bar';
      pillBar.setAttribute('role', 'tablist');
      pillBar.setAttribute('aria-label', 'Brand letters');

      sections.forEach((s, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lv-brand-pill';
        btn.textContent = s.isPopular ? 'All' : s.title;
        btn.dataset.letter = s.key;
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        btn.setAttribute('aria-controls', 'lv-brand-letter-' + s.key);
        pillBar.appendChild(btn);
      });

      // Search input (inline at right on desktop, in its own wrap)
      const searchWrap = document.createElement('div');
      searchWrap.className = 'lv-brand-search-wrap';
      const search = document.createElement('input');
      search.type = 'search';
      search.className = 'lv-brand-search';
      search.placeholder = 'Search brands';
      search.setAttribute('aria-label', 'Search brands');
      searchWrap.appendChild(search);

      // Build scroll area with sections
      const scrollArea = document.createElement('div');
      scrollArea.className = 'lv-brand-scroll-area';

      sections.forEach((s) => {
        let section;
        if (isMobile && !s.isPopular) {
          // Mobile non-popular letters → <details>
          section = document.createElement('details');
          section.className = 'lv-brand-section';
          if (s.isPopular) section.classList.add('lv-brand-section--popular');
          section.dataset.letter = s.key;
          section.id = 'lv-brand-letter-' + s.key;
          const summary = document.createElement('summary');
          summary.textContent = s.title;
          section.appendChild(summary);
        } else {
          section = document.createElement('section');
          section.className = 'lv-brand-section' + (s.isPopular ? ' lv-brand-section--popular' : '');
          section.dataset.letter = s.key;
          section.id = 'lv-brand-letter-' + s.key;
          const h3 = document.createElement('h3');
          h3.className = 'lv-brand-section__title';
          h3.textContent = s.title;
          section.appendChild(h3);
        }
        const ul = document.createElement('ul');
        ul.className = 'lv-brand-grid';
        s.brands.forEach((b) => {
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.className = 'lv-brand-link';
          a.href = b.href;
          a.textContent = b.name;
          li.appendChild(a);
          ul.appendChild(li);
        });
        section.appendChild(ul);
        scrollArea.appendChild(section);
      });

      // Insert into grid (before the original .mega-menu__list which CSS hides)
      const list = grid.querySelector('.mega-menu__list');
      if (isMobile) {
        // On mobile: pill bar → search → scroll area
        grid.insertBefore(pillBar, list || null);
        pillBar.appendChild(searchWrap);
        // Wait — search-wrap needs to be its own row on mobile. Move it out.
        grid.insertBefore(searchWrap, list || null);
        grid.insertBefore(scrollArea, list || null);
      } else {
        pillBar.appendChild(searchWrap);
        grid.insertBefore(pillBar, list || null);
        grid.insertBefore(scrollArea, list || null);
      }

      // Pill click → smooth-scroll to section (and auto-expand on mobile)
      pillBar.addEventListener('click', (e) => {
        if (!(e.target instanceof Element)) return;
        const btn = e.target.closest('.lv-brand-pill');
        if (!(btn instanceof HTMLElement)) return;
        const letter = btn.dataset.letter;
        if (!letter) return;

        pillBar.querySelectorAll('.lv-brand-pill').forEach((p) => p.setAttribute('aria-selected', 'false'));
        btn.setAttribute('aria-selected', 'true');

        const target = scrollArea.querySelector(`[data-letter="${letter}"]`);
        if (target instanceof HTMLElement) {
          if (target instanceof HTMLDetailsElement) target.open = true;
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, sig());

      // Search filter
      let searchTimer = null;
      search.addEventListener('input', () => {
        if (searchTimer) clearTimeout(searchTimer);
        searchTimer = setTimeout(() => {
          const q = search.value.trim().toLowerCase();
          const sections = scrollArea.querySelectorAll('.lv-brand-section');
          let anyMatch = false;
          sections.forEach((sec) => {
            if (!(sec instanceof HTMLElement)) return;
            const items = sec.querySelectorAll('.lv-brand-grid > li');
            let sectionHasMatch = false;
            items.forEach((li) => {
              const a = li.querySelector('a');
              const name = a ? a.textContent.toLowerCase() : '';
              const match = !q || name.includes(q);
              li.hidden = !match;
              if (match) sectionHasMatch = true;
            });
            sec.hidden = q && !sectionHasMatch;
            if (sectionHasMatch) anyMatch = true;
            // Auto-expand matching <details> on mobile during search
            if (sec instanceof HTMLDetailsElement && q && sectionHasMatch) {
              sec.open = true;
            }
          });
          // Hide pills for letters with no visible section
          pillBar.querySelectorAll('.lv-brand-pill').forEach((p) => {
            if (!(p instanceof HTMLElement)) return;
            const letter = p.dataset.letter;
            const sec = scrollArea.querySelector(`[data-letter="${letter}"]`);
            p.hidden = q && (!sec || sec.hidden);
          });
        }, 80);
      }, sig());

      // IntersectionObserver — auto-sync active pill as user scrolls
      const sectionEls = Array.from(scrollArea.querySelectorAll('.lv-brand-section'));
      if (sectionEls.length > 0 && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          // Find the topmost visible section
          let topmost = null;
          let topY = Infinity;
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const y = entry.boundingClientRect.top;
              if (y < topY) {
                topY = y;
                topmost = entry.target;
              }
            }
          });
          if (topmost instanceof HTMLElement && topmost.dataset.letter) {
            pillBar.querySelectorAll('.lv-brand-pill').forEach((p) => {
              if (!(p instanceof HTMLElement)) return;
              p.setAttribute('aria-selected', p.dataset.letter === topmost.dataset.letter ? 'true' : 'false');
            });
          }
        }, { root: scrollArea, rootMargin: '-10% 0px -70% 0px', threshold: 0 });
        sectionEls.forEach((s) => io.observe(s));
        rootAC.signal.addEventListener('abort', () => io.disconnect());
      }
    });
  }

  // ──────────────────────────────────────────────────────────────────────
  // §10.5 mobile — brand pill-bar inside the mobile drawer.
  //
  // The desktop initBrandPillBar() targets .mega-menu__grid which only
  // exists in the desktop mega-menu DOM. On mobile, "Shop by Brand" is
  // rendered by Horizon's slide-in drawer as:
  //
  //   #Details-menu-drawer-container           (outer drawer details)
  //     .menu-drawer__submenu                  (slide panel for "Shop by Brand")
  //       .menu-drawer__inner-submenu
  //         ul.menu-drawer__menu--childlist    (the Level-2 list)
  //           li.menu-drawer__list-item--flat  (per letter group, e.g. "Most Popular", "A" …)
  //             a.menu-drawer__menu-item--parent  (eyebrow label — section header)
  //             ul.menu-drawer__menu--grandchildlist  (actual brand links)
  //               li > a.menu-drawer__menu-item--child  (individual brand)
  //
  // Detection: find the drawer submenu whose back-button text includes
  // the word "Brand" (case-insensitive). Then look at the first --flat
  // item's --parent anchor text to confirm it starts with "most popular".
  //
  // The rebuilt DOM (pill bar + search + scroll area) is injected into
  // .menu-drawer__inner-submenu, after the nav-buttons row. The original
  // ul.menu-drawer__menu--childlist is hidden via [data-lv-drawer-brand-az].
  // CSS for the injected elements reuses all .lv-brand-* rules from §10.5b.
  //
  // Triggered via MutationObserver watching #Details-menu-drawer-container
  // for [open] attribute changes. Idempotent: bails if pill bar already present.
  // ──────────────────────────────────────────────────────────────────────
  function initBrandPillBarMobile() {
    // DISABLED 2026-05-14: the DOM rebuild on mobile was producing
    // bulleted lists + oversized h3 + empty letter sections (data
    // harvester wasn't matching Horizon's actual flat-drawer Level-3
    // markup reliably). Eyebrow caps in §10.6 CSS cover the highest-
    // impact mobile UX win without JS. Mobile pill-bar is deferred
    // pending live-DOM inspection. Early-return is the safest revert
    // while keeping the function signature in case we re-enable later.
    return;
    /* eslint-disable */
    const outerDrawer = document.querySelector('#Details-menu-drawer-container');
    if (!outerDrawer) return;

    function buildMobilePillBar(submenu) {
      // Idempotent guard
      if (submenu.querySelector('.lv-brand-pill-bar')) return;

      const innerSubmenu = submenu.querySelector('.menu-drawer__inner-submenu');
      if (!innerSubmenu) return;

      const childList = innerSubmenu.querySelector('ul.menu-drawer__menu--childlist');
      if (!childList) return;

      // Confirm this is the Brand submenu: first --flat item's label starts with "most popular"
      const firstFlat = childList.querySelector('.menu-drawer__list-item--flat');
      if (!firstFlat) return;
      const firstLabel = firstFlat.querySelector('.menu-drawer__menu-item--parent .menu-drawer__menu-item-text, .menu-drawer__menu-item--parent');
      const firstText = firstLabel ? firstLabel.textContent.trim().toLowerCase() : '';
      if (!firstText.startsWith('most popular')) return;

      // Collect all --flat sections into structured data
      const flatItems = Array.from(childList.querySelectorAll('.menu-drawer__list-item--flat'));
      const sections = [];
      flatItems.forEach((li) => {
        const parentLink = li.querySelector('.menu-drawer__menu-item--parent');
        const rawLabel = parentLink ? (parentLink.querySelector('.menu-drawer__menu-item-text') || parentLink).textContent.trim() : '';
        const isPopular = rawLabel.toLowerCase().startsWith('most popular');
        const letterKey = isPopular ? 'all' : rawLabel;
        const displayTitle = isPopular ? 'Most Popular' : rawLabel;
        const brandLinks = Array.from(li.querySelectorAll('.menu-drawer__menu--grandchildlist .menu-drawer__menu-item--child'));
        if (brandLinks.length === 0) return;
        const brands = brandLinks.map((a) => ({
          name: (a.querySelector('.menu-drawer__menu-item-text') || a).textContent.trim(),
          href: a.getAttribute('href') || '#',
        }));
        sections.push({ key: letterKey, title: displayTitle, brands, isPopular });
      });

      if (sections.length === 0) return;

      // Mark the child list so CSS §10.5b hides it and the grid triggers
      childList.dataset.lvDrawerBrandAz = '';
      childList.style.display = 'none';

      // Build pill bar
      const pillBar = document.createElement('div');
      pillBar.className = 'lv-brand-pill-bar';
      pillBar.setAttribute('role', 'tablist');
      pillBar.setAttribute('aria-label', 'Brand letters');

      sections.forEach((s, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lv-brand-pill';
        btn.textContent = s.isPopular ? 'All' : s.title;
        btn.dataset.letter = s.key;
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        btn.setAttribute('aria-controls', 'lv-mob-brand-' + s.key);
        pillBar.appendChild(btn);
      });

      // Search wrap
      const searchWrap = document.createElement('div');
      searchWrap.className = 'lv-brand-search-wrap';
      const search = document.createElement('input');
      search.type = 'search';
      search.className = 'lv-brand-search';
      search.placeholder = 'Search brands';
      search.setAttribute('aria-label', 'Search brands');
      searchWrap.appendChild(search);

      // Scroll area with sections
      const scrollArea = document.createElement('div');
      scrollArea.className = 'lv-brand-scroll-area';

      sections.forEach((s) => {
        let section;
        if (s.isPopular) {
          section = document.createElement('section');
          section.className = 'lv-brand-section lv-brand-section--popular';
          section.dataset.letter = s.key;
          section.id = 'lv-mob-brand-' + s.key;
          const h3 = document.createElement('h3');
          h3.className = 'lv-brand-section__title';
          h3.textContent = s.title;
          section.appendChild(h3);
        } else {
          section = document.createElement('details');
          section.className = 'lv-brand-section';
          section.dataset.letter = s.key;
          section.id = 'lv-mob-brand-' + s.key;
          const summary = document.createElement('summary');
          summary.textContent = s.title;
          section.appendChild(summary);
        }
        const ul = document.createElement('ul');
        ul.className = 'lv-brand-grid';
        s.brands.forEach((b) => {
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.className = 'lv-brand-link';
          a.href = b.href;
          a.textContent = b.name;
          li.appendChild(a);
          ul.appendChild(li);
        });
        section.appendChild(ul);
        scrollArea.appendChild(section);
      });

      // Insert after the nav-buttons row
      const navButtons = innerSubmenu.querySelector('.menu-drawer__nav-buttons');
      const insertAfter = navButtons || innerSubmenu.firstChild;
      if (navButtons) {
        navButtons.after(pillBar, searchWrap, scrollArea);
      } else {
        innerSubmenu.prepend(scrollArea, searchWrap, pillBar);
      }

      // Pill click: scroll target into view + expand details
      pillBar.addEventListener('click', (e) => {
        if (!(e.target instanceof Element)) return;
        const btn = e.target.closest('.lv-brand-pill');
        if (!(btn instanceof HTMLElement)) return;
        const letter = btn.dataset.letter;
        if (!letter) return;
        pillBar.querySelectorAll('.lv-brand-pill').forEach((p) => p.setAttribute('aria-selected', 'false'));
        btn.setAttribute('aria-selected', 'true');
        const target = scrollArea.querySelector('[data-letter="' + letter + '"]');
        if (target instanceof HTMLElement) {
          if (target instanceof HTMLDetailsElement) target.open = true;
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, sig());

      // Live search filter
      let searchTimer = null;
      search.addEventListener('input', () => {
        if (searchTimer) clearTimeout(searchTimer);
        searchTimer = setTimeout(() => {
          const q = search.value.trim().toLowerCase();
          const allSections = scrollArea.querySelectorAll('.lv-brand-section');
          allSections.forEach((sec) => {
            if (!(sec instanceof HTMLElement)) return;
            const items = sec.querySelectorAll('.lv-brand-grid > li');
            let hasMatch = false;
            items.forEach((li) => {
              const a = li.querySelector('a');
              const name = a ? a.textContent.toLowerCase() : '';
              const match = !q || name.includes(q);
              li.hidden = !match;
              if (match) hasMatch = true;
            });
            sec.hidden = !!(q && !hasMatch);
            if (sec instanceof HTMLDetailsElement && q && hasMatch) sec.open = true;
          });
          pillBar.querySelectorAll('.lv-brand-pill').forEach((p) => {
            if (!(p instanceof HTMLElement)) return;
            const sec = scrollArea.querySelector('[data-letter="' + p.dataset.letter + '"]');
            p.hidden = !!(q && (!sec || sec.hidden));
          });
        }, 80);
      }, sig());
    }

    // Triggers (belt + braces):
    //  (1) Click on the "Shop by Brand" summary → after 400ms (slide
    //      animation) scan for the visible submenu. Reliable.
    //  (2) MutationObserver on the outer drawer for menu-open class or
    //      [open] attribute on any inner <details>. Catches programmatic
    //      opens that don't go through summary click.
    //  Both call scanAndBuild which is idempotent via :scope >
    //  .lv-brand-pill-bar guard.

    function scanAndBuild() {
      // Find every submenu in the drawer; build into the one that looks
      // like Shop by Brand (first --flat item text starts with "most popular").
      outerDrawer.querySelectorAll('.menu-drawer__submenu').forEach((sub) => {
        if (sub instanceof HTMLElement) buildMobilePillBar(sub);
      });
    }

    // (1) Direct click handler on the summary buttons. Horizon's drawer
    // emits on:click="header-drawer/open/submenu" on summary[ref="parentDetails"]
    // children — see snippets/header-drawer.liquid:183.
    outerDrawer.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;
      const summary = e.target.closest('summary');
      if (!summary) return;
      // 400ms covers Horizon's slide animation (200ms ease) + DOM settle.
      setTimeout(() => requestAnimationFrame(scanAndBuild), 400);
    }, sig());

    // (2) Backup MutationObserver. Watches class + open attribute on the
    // subtree. Triggers scanAndBuild whenever any descendant <details>
    // toggles open OR adds the .menu-open class.
    const mo = new MutationObserver((mutations) => {
      let touched = false;
      for (const m of mutations) {
        if (!(m.target instanceof Element)) continue;
        // Skip mutations inside our own rail to avoid loops
        if (m.target.closest('.lv-brand-pill-bar, .lv-brand-scroll-area, .lv-brand-search-wrap')) continue;
        // Care about: <details>[open] toggles, .menu-open class toggles,
        // anything on a .menu-drawer__submenu element
        if (m.attributeName === 'open' || m.attributeName === 'class') {
          touched = true;
          break;
        }
      }
      if (touched) requestAnimationFrame(scanAndBuild);
    });
    mo.observe(outerDrawer, { attributes: true, subtree: true, attributeFilter: ['open', 'class'] });
    rootAC.signal.addEventListener('abort', () => mo.disconnect());

    // (3) Final safety net: try once on init, in case the drawer is
    // already open with the brand submenu visible (e.g. theme editor preview).
    requestAnimationFrame(scanAndBuild);
    /* eslint-enable */
  }

  // Re-init the brand pill-bar whenever Horizon re-renders the mega-menu DOM
  // (Section Rendering API on first open, theme editor changes, etc.).
  // initBrandPillBar is idempotent via the :scope > .lv-brand-pill-bar guard.
  function watchForBrandMenuRender() {
    const header = document.querySelector('#header-component') || document.body;
    let queued = false;
    const mo = new MutationObserver((mutations) => {
      let touched = false;
      for (const m of mutations) {
        if (!(m.target instanceof Element)) continue;
        const t = m.target;
        if (t.classList?.contains('lv-brand-pill-bar')) continue;
        if (t.closest?.('.lv-brand-pill-bar, .lv-brand-scroll-area, .lv-brand-search-wrap')) continue;
        if (
          t.classList?.contains('mega-menu__grid') ||
          t.querySelector?.('.mega-menu__grid') ||
          t.closest?.('.mega-menu__grid')
        ) {
          touched = true;
          break;
        }
      }
      if (!touched) return;
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        initBrandPillBar();
      });
    });
    mo.observe(header, { childList: true, subtree: true });
    rootAC.signal.addEventListener('abort', () => mo.disconnect());
  }

  function boot() {
    initStaggeredReveal();
    initSubmenuScrollSnap();
    initBrassLineTap();
    initMegaMenuFadeIn();
    initBrassIndicator();
    initDrawerBodyLock();
    initHoverIntent();
    initStickyShowHide();
    initBrandPillBar();
    initBrandPillBarMobile();
    watchForBrandMenuRender();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
